import enviro from 'enviro';
import Raven from 'raven-js';
import { captureException } from '../ravenUtils';
import {
  Checks,
  ReporterAction,
  ErrorAttributes,
  SoftNavigationContext,
} from '../types';
import { GlobalErrorMetrics } from '../Metrics';
import { UNEXPECTED_ROUTE_ERROR } from '../Constants';
import { getStaticAppName, getStaticAppVersion } from '../staticAppInfo';

export interface StaticAppInfo {
  staticAppName: string;
  staticAppVersion: string;
}

export interface ReporterOptions {
  timingOffset?: number;
  staticAppInfo?: StaticAppInfo;
  userId?: string | null;
  softNavigationContext?: SoftNavigationContext;
  isGraniteEnabled?: boolean;
  isGraniteRemoteApp?: boolean;
}

export interface NavigationReporterOptions extends ReporterOptions {
  softNavigationContext: SoftNavigationContext | undefined;
}

export const Color = {
  OBSIDIAN: '#33475b',
  OZ: '#00bda5',
  OZ_LIGHT: '#e5f8f6',
  OZ_DARK: '#00a38d',
  CANDY_APPLE_DARK: '#d94c53',
  SLINKY: '#516f90',
  KOALA: '#eaf0f6',
  MARIGOLD: '#f5c26b',
  MARIGOLD_LIGHT: '#fef8f0',
  CANDY_APPLE_LIGHT: '#fdedee',
  CANDY_APPLE: '#f2545b',
  THUNDERDOME: '#6a78d1',
  THUNDERDOME_LIGHT: '#f0f1fa',
} as const;

export type TColor = (typeof Color)[keyof typeof Color];

export default class BaseReporter {
  options: ReporterOptions;
  debug: boolean;
  libName: string;
  staticAppName: string;
  staticAppVersion: string;
  softNavigationContext: SoftNavigationContext | undefined;
  private resolved: { [id: number]: boolean } = {};

  constructor(options: ReporterOptions = {}) {
    this.options = options;
    this.staticAppName = getStaticAppName(options.staticAppInfo);
    this.staticAppVersion = getStaticAppVersion(options.staticAppInfo);
    this.debug =
      enviro.debug('react-rhumb') === 'true' ||
      enviro.debug(`${this.staticAppName}.react-rhumb`) === 'true';
    this.libName = `react-rhumb`;
    this.softNavigationContext = options.softNavigationContext;
  }

  getAdjustedDurationMs(successTimestamp: number, fallbackMs: number): number {
    return this.softNavigationContext
      ? successTimestamp - this.softNavigationContext.timestamp
      : fallbackMs;
  }

  performanceMark(name: string, markOptions?: PerformanceMarkOptions) {
    if (typeof performance.mark === 'function') {
      performance.mark(name, markOptions);
    }
  }

  performanceEntries() {
    if (typeof performance.getEntries === 'function') {
      return performance.getEntries() || [];
    }

    return [];
  }

  toTimings(checks: Checks, baseTime: number, markers: string[]) {
    let selector;
    let longest = -Infinity;
    markers.forEach((marker) => {
      if (
        Object.prototype.hasOwnProperty.call(checks, marker) &&
        checks[marker].timestamp > longest
      ) {
        longest = checks[marker].timestamp;
        selector = marker;
      }
    });
    return {
      duration: Math.max(0, longest - baseTime),
      timestamp: Math.max(0, longest),
      selector,
    };
  }

  setCustomAttribute(attributeName: string, attributeValue: any) {
    Raven.setExtraContext({ [attributeName]: String(attributeValue) });
  }

  captureError(error: Error, attributes?: ErrorAttributes) {
    let data = {};
    let tags = {};

    if (attributes) {
      data = attributes.data;
      tags = attributes.tags;
    }

    captureException(error, {
      extra: data,
      tags,
    });

    GlobalErrorMetrics.counter('captured').increment();
  }

  protected isResolved(id: number): boolean {
    return !!this.resolved[id];
  }

  protected markResolved(id: number): void {
    this.resolved[id] = true;
  }

  report(__action: ReporterAction) {
    throw new Error('Reporters must define a custom report() function');
  }

  updateUserId?(__userId: string | null): void;

  destroy(): void {}

  labelCss(background: TColor, border: TColor) {
    return `background-color:${background};color:${Color.OBSIDIAN};padding: 0 .5rem;border-left: 4px solid ${border};`;
  }

  colorCss(val: TColor) {
    return `color:${val};`;
  }

  logGroupWithBadge(
    title: string,
    lightColor: TColor,
    darkColor: TColor,
    groupContents: () => void
  ) {
    let loggedAppName = this.libName;
    try {
      // If we're logging from an app within an iframe, include the app name in the log line
      if (window.self !== window.top) {
        loggedAppName += ` (child frame: ${this.staticAppName})`;
      }
    } catch (error) {
      // Accessing window.top can throw an error if the iframe and parent window have different domains,
      // so an error thrown here is another indicator that this is being called from within an iframe
      loggedAppName += ` (child frame: ${this.staticAppName})`;
    }

    console.groupCollapsed(
      `%c${loggedAppName}%c ${title}`,
      this.labelCss(lightColor, darkColor),
      ''
    );
    groupContents();
    console.groupEnd();
  }

  captureUnexpectedRoute(pathname: string) {
    captureException(UNEXPECTED_ROUTE_ERROR, {
      level: 'error',
      tags: {
        pathname,
      },
    });
  }
}
